import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import { useUndoOptional } from "@/lib/undo/UndoContext";
import type {
  DataResponse,
  PoradieDielcovResponse,
  PoradieZostavResponse,
  ZakazkaDetail,
  Zostava,
  ZostavaCreate,
  ZostavaDuplicate,
  ZostavaUpdate,
} from "@/api/schemas";

/**
 * Detail zákazky aj súhrny vpravo (kovania, hrany) — tie rátajú × kusy
 * zostavy (KOLO 216), takže ich mení aj Ks, duplikát a zmazanie zostavy.
 * Do KOLA 216 sa obnovoval len detail a panel ukazoval staré počty.
 */
function obnovZakazku(
  qc: ReturnType<typeof useQueryClient>,
  zakazkaId: string,
): Promise<unknown> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: queryKeys.zakazky.detail(zakazkaId) }),
    qc.invalidateQueries({ queryKey: queryKeys.varianty.kovaniaSummaries() }),
    qc.invalidateQueries({ queryKey: queryKeys.varianty.edgesSummaries() }),
  ]);
}

export function useCreateZostava(
  zakazkaId: string,
  variantaId: string,
): UseMutationResult<DataResponse<Zostava>, Error, ZostavaCreate> {
  const qc = useQueryClient();
  const undo = useUndoOptional();
  return useMutation({
    mutationFn: async (payload: ZostavaCreate) => {
      const { data } = await api.post<DataResponse<Zostava>>(
        `/varianty/${variantaId}/zostavy`,
        payload,
      );
      return data;
    },
    onSuccess: (vysledok) => {
      undo?.pridaj({
        popis: `pridanie zostavy ${vysledok.data.kod}`,
        vrat: async () => {
          await api.delete(`/zostavy/${vysledok.data.id}`);
          await obnovZakazku(qc, zakazkaId);
        },
      });
      void obnovZakazku(qc, zakazkaId);
    },
  });
}

export function useUpdateZostava(
  zakazkaId: string,
): UseMutationResult<
  DataResponse<Zostava>,
  Error,
  { id: string; data: ZostavaUpdate }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.patch<DataResponse<Zostava>>(
        `/zostavy/${id}`,
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      void obnovZakazku(qc, zakazkaId);
    },
  });
}

export function useDeleteZostava(
  zakazkaId: string,
): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  const undo = useUndoOptional();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/zostavy/${id}`);
    },
    onSuccess: (_data, id) => {
      // Obnovenie vráti aj dielce zmazané spolu so zostavou — ale len tie
      // (backend porovnáva časovú pečiatku).
      undo?.pridaj({
        popis: "zmazanie zostavy",
        vrat: async () => {
          await api.post(`/zostavy/${id}/obnovit`);
          await obnovZakazku(qc, zakazkaId);
        },
      });
      void obnovZakazku(qc, zakazkaId);
    },
  });
}

export function useDuplicateZostava(
  zakazkaId: string,
): UseMutationResult<
  DataResponse<Zostava>,
  Error,
  { id: string; data: ZostavaDuplicate }
> {
  const qc = useQueryClient();
  const undo = useUndoOptional();
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.post<DataResponse<Zostava>>(
        `/zostavy/${id}/duplicate`,
        data,
      );
      return response.data;
    },
    onSuccess: (vysledok) => {
      undo?.pridaj({
        popis: `duplikovanie zostavy ${vysledok.data.kod}`,
        vrat: async () => {
          await api.delete(`/zostavy/${vysledok.data.id}`);
          await obnovZakazku(qc, zakazkaId);
        },
      });
      void obnovZakazku(qc, zakazkaId);
    },
  });
}

/** Riadky zostavy v cache v novom poradí — tabuľka ich ukáže hneď. */
function preusporiadajVCache(
  zakazka: ZakazkaDetail,
  zostavaId: string,
  dielce: readonly string[],
): ZakazkaDetail {
  const index = new Map(dielce.map((id, i) => [id, i]));
  const varianty = (zakazka.varianty ?? []).map((v) => ({
    ...v,
    zostavy: (v.zostavy ?? []).map((z) =>
      z.id !== zostavaId
        ? z
        : {
            ...z,
            // Vrstvy a paušál ostávajú za tým (strom ich viaže na rodiča)
            dielce: [...(z.dielce ?? [])].sort(
              (a, b) =>
                (index.get(a.id) ?? Number.MAX_SAFE_INTEGER) -
                (index.get(b.id) ?? Number.MAX_SAFE_INTEGER),
            ),
          },
    ),
  }));
  return { ...zakazka, varianty } as ZakazkaDetail;
}

/**
 * Posun riadkov v zostave (KOLO 176) — ťahanie v tabuľke a Alt+↑/↓.
 *
 * Optimisticky preusporiada cache (kódy dobehnú s odpoveďou servera),
 * pri chybe ju vráti. Ctrl+Z je **jeden krok**: pošle `predtym` z odpovede
 * na ten istý endpoint a server vráti presne pôvodné poradie.
 */
export function usePoradieDielcov(
  zakazkaId: string,
): UseMutationResult<
  DataResponse<PoradieDielcovResponse>,
  Error,
  { zostavaId: string; dielce: string[]; popis?: string },
  { previous: DataResponse<ZakazkaDetail> | undefined }
> {
  const qc = useQueryClient();
  const undo = useUndoOptional();
  const key = queryKeys.zakazky.detail(zakazkaId);
  return useMutation({
    mutationFn: async ({ zostavaId, dielce }) => {
      const { data } = await api.post<DataResponse<PoradieDielcovResponse>>(
        `/zostavy/${zostavaId}/poradie-dielcov`,
        { dielce },
      );
      return data;
    },
    onMutate: async ({ zostavaId, dielce }) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<DataResponse<ZakazkaDetail>>(key);
      if (previous) {
        qc.setQueryData<DataResponse<ZakazkaDetail>>(key, {
          ...previous,
          data: preusporiadajVCache(previous.data, zostavaId, dielce),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) qc.setQueryData(key, context.previous);
    },
    onSuccess: (vysledok, { zostavaId, popis }) => {
      const predtym = vysledok.data.predtym;
      undo?.pridaj({
        popis: popis ?? "posunutie riadku",
        vrat: async () => {
          await api.post(`/zostavy/${zostavaId}/poradie-dielcov`, { dielce: predtym });
          await qc.invalidateQueries({ queryKey: key });
        },
      });
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: key });
    },
  });
}


/** Zostavy varianty v cache v novom poradí — tabuľka ich ukáže hneď. */
function preusporiadajZostavyVCache(
  zakazka: ZakazkaDetail,
  variantaId: string,
  zostavy: readonly string[],
  celky?: Record<string, string | null>,
): ZakazkaDetail {
  const index = new Map(zostavy.map((id, i) => [id, i]));
  const varianty = (zakazka.varianty ?? []).map((v) =>
    v.id !== variantaId
      ? v
      : {
          ...v,
          zostavy: [...(v.zostavy ?? [])]
            // Celok zostavy (KOLO 235) — pás potiahnutý do iného celku
            .map((z) => (celky && z.id in celky ? { ...z, celok_id: celky[z.id] } : z))
            .sort(
              (a, b) =>
                (index.get(a.id) ?? Number.MAX_SAFE_INTEGER) -
                (index.get(b.id) ?? Number.MAX_SAFE_INTEGER),
            ),
        },
  );
  return { ...zakazka, varianty } as ZakazkaDetail;
}

/**
 * Posun zostáv vo variante (KOLO 183) — ťahanie pásu zostavy a Alt+↑/↓.
 *
 * To isté ako `usePoradieDielcov`: optimisticky preusporiada cache (kódy
 * A, B, C dobehnú s odpoveďou), pri chybe ju vráti a Ctrl+Z je jeden krok
 * — pošle `predtym` z odpovede na ten istý endpoint.
 */
export function usePoradieZostav(
  zakazkaId: string,
): UseMutationResult<
  DataResponse<PoradieZostavResponse>,
  Error,
  {
    variantaId: string;
    zostavy: string[];
    /** Nový celok zostáv, ktorým sa mení (KOLO 235); `null` = bez celku. */
    celky?: Record<string, string | null>;
    popis?: string;
  },
  { previous: DataResponse<ZakazkaDetail> | undefined }
> {
  const qc = useQueryClient();
  const undo = useUndoOptional();
  const key = queryKeys.zakazky.detail(zakazkaId);
  return useMutation({
    mutationFn: async ({ variantaId, zostavy, celky }) => {
      const { data } = await api.post<DataResponse<PoradieZostavResponse>>(
        `/varianty/${variantaId}/zostavy/poradie`,
        celky ? { zostavy, celky } : { zostavy },
      );
      return data;
    },
    onMutate: async ({ variantaId, zostavy, celky }) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<DataResponse<ZakazkaDetail>>(key);
      if (previous) {
        qc.setQueryData<DataResponse<ZakazkaDetail>>(key, {
          ...previous,
          data: preusporiadajZostavyVCache(previous.data, variantaId, zostavy, celky),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) qc.setQueryData(key, context.previous);
    },
    onSuccess: (vysledok, { variantaId, popis }) => {
      const { predtym, predtym_celky: predtymCelky } = vysledok.data;
      undo?.pridaj({
        popis: popis ?? "posunutie zostavy",
        vrat: async () => {
          await vratPoradieZostav(variantaId, predtym, predtymCelky);
          await qc.invalidateQueries({ queryKey: key });
        },
      });
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: key });
    },
  });
}

/**
 * Krok späť poradia zostáv (KOLO 183) aj s ich celkami (KOLO 235) —
 * `predtym` a `predtym_celky` z odpovede idú na ten istý endpoint.
 */
export async function vratPoradieZostav(
  variantaId: string,
  predtym: string[],
  predtymCelky?: Record<string, string | null> | null,
): Promise<void> {
  const celky = predtymCelky && Object.keys(predtymCelky).length > 0 ? predtymCelky : null;
  await api.post(
    `/varianty/${variantaId}/zostavy/poradie`,
    celky ? { zostavy: predtym, celky } : { zostavy: predtym },
  );
}
